import { DateValue } from '@internationalized/date';
import { Color, Spacing } from '@persona-id/design-tokens';
import { DatabaseIcon } from '@persona-id/design-tokens/icons';
import { RangeValue } from '@react-types/shared';
import { groupBy, sortBy } from 'lodash/fp';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { MEDIA_WIDTHS, mediaQuery } from 'shared/styles/variables';
import styled from 'styled-components';

import AnalyticsDatePicker from 'dashboard/components/AnalyticsDatePicker';
import { AnalyticsDateRange } from 'dashboard/components/AnalyticsDatePicker/AnalyticsDatePicker';
import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import ReactMultiSelect from 'dashboard/components/ReactMultiSelect';
import { generatePlaceHolderDisplay } from 'dashboard/components/ReactMultiSelect/ReactMultiSelect';
import ReactSelect, { OptionType } from 'dashboard/components/ReactSelect';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import { userDisplayName } from 'dashboard/lib/valueDisplayers/userDisplayName';
import {
  CaseTemplateBasicsOption,
  CaseTemplateBasicsOptionState,
} from 'dashboard/models/case-template';
import { Tag } from 'dashboard/models/tag';
import { Team } from 'dashboard/models/team';
import { User, UserStatus } from 'dashboard/models/user';
import { CaseAnalyticsFilterDateType } from 'dashboard/redux/actions/case-analytics';
import { CaseAnalyticsState } from 'dashboard/redux/reducers/case-analytics';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import { HStack } from 'design-system/components/Stack';
import { Paragraph } from 'design-system/components/Text';
import { TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import { Iso8601DateString } from 'lib/types';

const SearchAndFilterCardBody = styled(Card.Body)`
  grid-gap: 0;
  grid-template-columns: 1fr;
  align-items: start;

  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    grid-template-columns: none;
    grid-template-rows: auto auto;
  }
`;

const SearchAndFilterCardTitle = styled(Card.Title)`
  grid-column: 1 / flex 2;
`;

const RunButton = styled(Button)`
  height: ${Spacing[6]};
`;

const singleSelectStyles = {
  control: () => ({
    minHeight: `${Spacing[6]}`,
    height: `${Spacing[6]}`,
  }),
  container: () => ({
    minHeight: `${Spacing[6]}`,
  }),
  valueContainer: () => ({
    height: `${Spacing[6]}`,
  }),
  indicatorsContainer: () => ({
    height: `${Spacing[6]}`,
  }),
  menuPortal: () => ({
    height: `${Spacing[6]}`,
  }),
};

const customStyles = {
  control: () => ({
    minHeight: `${Spacing[6]}`,
  }),
  container: () => ({
    minHeight: `${Spacing[6]}`,
  }),
  valueContainer: () => ({
    height: `${Spacing[6]}`,
  }),
  indicatorsContainer: () => ({
    height: `${Spacing[6]}`,
  }),
};

const placeholderCustomStyles = {
  placeholder: () => ({ color: Color.CoreSlate1000 }),
  control: () => ({
    minHeight: `${Spacing[6]}`,
  }),
  container: () => ({
    minHeight: `${Spacing[6]}`,
  }),
  valueContainer: () => ({
    height: `${Spacing[6]}`,
  }),
  indicatorsContainer: () => ({
    height: `${Spacing[6]}`,
  }),
};

const SearchAndFilterContainer = styled.div`
  flex: auto;
  display: grid;
  align-items: end;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
  grid-gap: ${Spacing[4]};

  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    grid-template-columns: none;
    grid-template-rows: auto auto;
  }
`;

const StyledInfoIcon = styled(DatabaseIcon)`
  margin-bottom: -${Spacing[1]};
`;

const DatePickerContainer = styled.div`
  grid-column-start: 2;
  grid-column-end: 4;
`;

const DATE_TYPE_OPTIONS = [
  {
    label: 'Created at',
    value: CaseAnalyticsFilterDateType.CreatedAt,
  },
  {
    label: 'Resolved at',
    value: CaseAnalyticsFilterDateType.ResolvedAt,
  },
];

export interface CaseAnalyticsFiltersProps
  extends Pick<CaseAnalyticsState, 'filters' | 'updatedAt'> {
  activeCaseTemplateBasicsOptions: CaseTemplateBasicsOption[];
  tags: Tag[];
  users: User[];
  teams: Team[];
  queriedAt: Iso8601DateString;
  loading: boolean;
  loadAnalytics: () => void;
  handleDateTypeUpdate: (type: CaseAnalyticsFilterDateType) => void;
  handleCaseTemplateFilterUpdate: (caseTemplateIds: string[]) => void;
  handleCaseTagFilterUpdate: (caseTagIds: string[]) => void;
  handleAssigneeFilterUpdate: (userAssigneeIds: string[], teamAssigneeIds: string[]) => void;
  handleResolverFilterUpdate: (userResolverIds: string[], teamResolverIds: string[]) => void;
  handleStatusFilterChange: (stateIds: string[]) => void;
  handleDateUpdateV2: (range: RangeValue<DateValue> | null) => void;
}

const CaseAnalyticsFilters = ({
  activeCaseTemplateBasicsOptions,
  tags,
  users,
  teams,
  queriedAt,
  updatedAt,
  filters,
  loading,
  loadAnalytics,
  handleDateTypeUpdate,
  handleCaseTemplateFilterUpdate,
  handleCaseTagFilterUpdate,
  handleAssigneeFilterUpdate,
  handleResolverFilterUpdate,
  handleStatusFilterChange,
  handleDateUpdateV2,
}: CaseAnalyticsFiltersProps) => {
  const {
    startDate,
    endDate,
    dateType,
    caseTemplateIds,
    caseTagIds,
    caseStateIds,
    assigneeIds,
    teamAssigneeIds,
    resolverIds,
    teamResolverIds,
  } = filters;
  const dateRange = useMemo<AnalyticsDateRange>(() => {
    return {
      startDate: startDate ? DateTime.fromISO(startDate, { setZone: true }) : undefined,
      endDate: endDate ? DateTime.fromISO(endDate, { setZone: true }) : undefined,
    };
  }, [startDate, endDate]);

  const activeOrgUsers = useMemo(() => {
    return users ? users.filter((user: User) => user.attributes.status === UserStatus.Active) : [];
  }, [users]);

  const activeOrgTeams = useMemo(() => {
    return teams ? teams.filter((team: Team) => team.attributes.status === 'active') : [];
  }, [teams]);

  const caseTemplateFilterOptions = useMemo(
    () =>
      activeCaseTemplateBasicsOptions.map((caseTemplateBasicsOption) => ({
        label: `${caseTemplateBasicsOption.name} (${caseTemplateBasicsOption['case-prefix']})`,
        value: caseTemplateBasicsOption.id,
      })),
    [activeCaseTemplateBasicsOptions],
  );

  const caseTagFilterOptions = useMemo(
    () =>
      tags.map((tag) => ({
        label: tag.attributes.name,
        value: tag.id,
      })),
    [tags],
  );

  const userFilterOptions = useMemo(
    () =>
      activeOrgUsers
        .map((user) => ({
          label: userDisplayName(user),
          value: user.id,
        }))
        .filter((userOption) => !(userOption.label == null))
        .sort((o1, o2) => (o1.label as string).localeCompare(o2.label as string)),
    [activeOrgUsers],
  );

  const teamFilterOptions = useMemo(
    () =>
      activeOrgTeams
        .map((team) => ({
          label: team.attributes.name,
          value: team.id,
        }))
        .sort((o1, o2) => o1.label.localeCompare(o2.label)),
    [activeOrgTeams],
  );

  const assigneeFilterOptions: OptionType<string | undefined>[] = useMemo(
    () => [
      {
        label: 'Teams',
        value: undefined,
        options: teamFilterOptions,
      },
      {
        label: 'Users',
        value: undefined,
        options: userFilterOptions,
      },
    ],
    [userFilterOptions, teamFilterOptions],
  );

  const stateFilterOptions = useMemo(() => {
    const states = sortBy(
      (x: CaseTemplateBasicsOptionState) => x.name,
      activeCaseTemplateBasicsOptions.flatMap(
        (caseTemplateBasicsOption) => caseTemplateBasicsOption.states,
      ),
    );

    const statesGroupByName = groupBy((x) => x.name, states);

    const options = Object.keys(statesGroupByName).map((stateName: string) => ({
      label: stateName,
      value: statesGroupByName[stateName].map((state) => state.token).join(','),
    }));

    return options;
  }, [activeCaseTemplateBasicsOptions]);

  return (
    <Card>
      <SearchAndFilterCardBody>
        <SearchAndFilterCardTitle>Query</SearchAndFilterCardTitle>
        <SearchAndFilterContainer>
          <div>
            <FormControlLabel>
              {`Dates (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`}
            </FormControlLabel>
            <ReactSelect
              value={dateType}
              options={DATE_TYPE_OPTIONS}
              onChange={handleDateTypeUpdate}
              disabled={loading}
              id='analytics-cases-view__date-type'
              data-test='analytics-cases-view__date-type'
              styles={singleSelectStyles}
            />
          </div>
          <DatePickerContainer>
            <AnalyticsDatePicker
              dataTest='analytics-cases-view__date-range'
              handleDatesChangeV2={handleDateUpdateV2}
              dateRange={dateRange}
              disabled={loading}
              label=''
            />
          </DatePickerContainer>
          {caseTemplateFilterOptions.length >= 1 && (
            <div>
              <FormControlLabel>Case templates</FormControlLabel>
              <ReactMultiSelect
                value={caseTemplateIds}
                options={caseTemplateFilterOptions}
                placeholderGenerator={generatePlaceHolderDisplay({
                  label: { single: 'case template', multiple: 'case templates' },
                  emptyIsAll: true,
                })}
                onChange={handleCaseTemplateFilterUpdate}
                disabled={loading}
                selectAllEnabled
                searchEnabled
                controlShouldRenderValue={false}
                hideSelectedOptions={false}
                styles={customStyles}
              />
            </div>
          )}
          {(userFilterOptions.length >= 1 || teamFilterOptions.length >= 1) && (
            <>
              <div>
                <FormControlLabel>Assignees</FormControlLabel>
                <ReactMultiSelect
                  preserveOrder={true}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  options={assigneeFilterOptions}
                  searchEnabled
                  controlShouldRenderValue={false}
                  hideSelectedOptions={false}
                  disabled={loading}
                  selectAllEnabled
                  selectAllLabel='All manual assignees'
                  styles={placeholderCustomStyles}
                  placeholderGenerator={generatePlaceHolderDisplay({
                    label: { single: 'assignee', multiple: 'assignees' },
                    emptyIsAll: true,
                  })}
                  onChange={(assigneeIds: string[]) => {
                    const teamIds = assigneeIds.filter((assigneeId) =>
                      assigneeId.startsWith('team_'),
                    );
                    const userIds = assigneeIds.filter((assigneeId) =>
                      assigneeId.startsWith('user_'),
                    );
                    handleAssigneeFilterUpdate(userIds, teamIds);
                  }}
                  value={teamAssigneeIds.concat(assigneeIds)}
                />
              </div>
              <div>
                <FormControlLabel>Resolvers</FormControlLabel>
                <ReactMultiSelect
                  isMulti
                  preserveOrder={true}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  options={assigneeFilterOptions}
                  disabled={loading}
                  searchEnabled
                  controlShouldRenderValue={false}
                  hideSelectedOptions={false}
                  selectAllEnabled
                  selectAllLabel='All manual resolvers'
                  styles={placeholderCustomStyles}
                  placeholderGenerator={generatePlaceHolderDisplay({
                    label: { single: 'resolver', multiple: 'resolvers' },
                    emptyIsAll: true,
                  })}
                  onChange={(resolverIds: string[]) => {
                    const teamIds = resolverIds.filter((resolverId) =>
                      resolverId.startsWith('team_'),
                    );
                    const userIds = resolverIds.filter((resolverId) =>
                      resolverId.startsWith('user_'),
                    );
                    handleResolverFilterUpdate(userIds, teamIds);
                  }}
                  value={teamResolverIds.concat(resolverIds)}
                />
              </div>
            </>
          )}
          <div>
            <FormControlLabel>Statuses</FormControlLabel>
            <ReactMultiSelect
              value={caseStateIds}
              options={stateFilterOptions}
              placeholderGenerator={generatePlaceHolderDisplay({
                label: { single: 'status', multiple: 'statuses' },
                emptyIsAll: true,
              })}
              onChange={handleStatusFilterChange}
              disabled={loading}
              styles={customStyles}
              selectAllEnabled
              searchEnabled
            />
          </div>
          <div>
            <FormControlLabel>Case tags</FormControlLabel>
            <ReactMultiSelect
              value={caseTagIds}
              options={caseTagFilterOptions}
              placeholderGenerator={generatePlaceHolderDisplay({
                label: { single: 'case tag', multiple: 'case tags' },
                emptyIsAll: true,
              })}
              onChange={handleCaseTagFilterUpdate}
              styles={customStyles}
              disabled={loading}
              selectAllEnabled
              searchEnabled
            />
          </div>
          <HStack align='center'>
            <RunButton
              id='analytics-cases-view__run'
              data-test='analytics-cases-view__run'
              onClick={loadAnalytics}
              isPending={loading}
              disabled={loading}
              width='auto'
            >
              Run
            </RunButton>
            {queriedAt != null && (
              <Paragraph size='sm' color='secondary'>
                {updatedAt != null && (
                  <HelpIcon icon={<StyledInfoIcon />}>
                    Data is up to date as of {DateTime.fromISO(updatedAt).toRelative()}
                  </HelpIcon>
                )}
                Last ran {DateTime.fromISO(queriedAt).toRelative()}
              </Paragraph>
            )}
          </HStack>
        </SearchAndFilterContainer>
      </SearchAndFilterCardBody>
    </Card>
  );
};

export default CaseAnalyticsFilters;
